import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  TableContainer, 
  TableToolbar, TableToolbarContent, TableToolbarSearch, Button, Modal,
  OverflowMenu, OverflowMenuItem,
  TextInput, FormGroup,
  InlineLoading, InlineNotification
} from "@carbon/react";
import { Add, Edit, TrashCan, Filter } from "@carbon/icons-react";
import { callApi } from "../../services/apiService";
import { callMongoApi } from "../../services/mongoApiService";
import { useNavigate } from "react-router-dom";
import { Tag, } from "@carbon/react";
import { Tabs, TabList, Tab, } from "@carbon/react";

import PropertyTable from "../../components/property/management/PropertyTable";
import PropertyFilters from "../../components/property/management/PropertyFilters";
import PropertyVerifyModal from "../../components/property/management/modals/PropertyVerifyModal";
import AddModal from "../../components/property/management/modals/AddModal";
import ContactModal from "../../components/property/management/modals/ContactModal";
import TransactionModal from "../../components/property/management/modals/PropertyTransactionModal";


const PROPERTY_TYPES = {
  Residential: ["Rent", "Resale", "PG/Hostel", "Flatmates"],
  Commercial: ["Rent", "Sale"],
  "Land/Plot": ["Resale"],
};

const ATTR_MAPPING = {
  postingBy: "posted_by",
  contactPerson: "contact_person",
  //shareOwnerNumber: "share_owner_number",
  contactPersonNumber: "contact_person_number",
  verificationStatus: "verification_status",
  measurementUnit: "price_measurement_unit_label",
  tags: "property_tag"
};

const UNDER_VERIFICATION_PARAMS = [
  "Under Verification",
  "under_verification",
];

const isUnderVerification = (v) =>
  String(v || "").toLowerCase().trim().replace(/[\s-]+/g, "_") === "under_verification";

const headers = [
  { key: "sequenceID", header: "Property ID" },
  { key: "propertyTitle", header: "Property Title" },
  { key: "postedType", header: "Property Type" },      
  { key: "postedBy", header: "Posted By" },            
  { key: "priceInfo", header: "Price Info" },
  { key: "paymentStatus", header: "Payment Status" },
  { key: "tag", header: "Tag" },
  { key: "location", header: "Location" },
  { key: "verificationStatus", header: "Verification Status" },
  { key: "verifiedBy", header: "Verified By" },
  { key: "verifiedDate", header: "Verified Date" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdAt", header: "Created At" },
  { key: "actions", header: "Actions" },
];

// SessionStorage keys
const STORAGE_KEYS = {
  FILTERS: 'property_filters',
  TAB: 'property_active_tab',
  SEARCH: 'property_search_term',
  PAGE: 'property_page',
  PAGE_SIZE: 'property_page_size'
};

// Utility functions for sessionStorage
const saveToStorage = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Failed to save to sessionStorage:', e);
  }
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load from sessionStorage:', e);
  }
  return defaultValue;
};

// Normalize verification status to the format the backend expects (snake_case, lowercase)
// Normalize verification status to the format the backend expects (snake_case, lowercase)
const normalizeVerificationStatus = (val) => {
  if (!val) return "";
  const v = String(val).toLowerCase().trim();

  // Map every possible human label / variant to the canonical backend value
  if (v === "under verification" || v === "under_verification") return "Under_Verification";
  if (v === "re verification" || v === "re-verification" || v === "re_verification") return "re-verification";
  if (v === "pending for verification" || v === "pending") return "pending";
  if (v === "draft") return "draft";
  if (v === "verified") return "Verified";

  // Fallback: replace spaces/hyphens with underscores
  return v.replace(/[\s-]+/g, "_").toUpperCase();
};

// Format verification status for display (e.g. "Under_Verification" -> "Under Verification")
const formatVerificationStatus = (val) => {
  if (!val) return "N/P";
  const v = String(val).trim();
  const key = v.toLowerCase().replace(/[\s-]+/g, "_");

  const map = {
    under_verification: "Under Verification",
    re_verification: "Re-Verification",
    pending_for_verification: "Pending For Verification",
    pending: "Pending",
    draft: "Draft",
    verified: "Verified",
  };

  if (map[key]) return map[key];

  // Fallback: replace underscores/hyphens with spaces and Title Case
  return v
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};


export default function Property() {
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  // Load initial state from sessionStorage
  const loadInitialFilters = () => {
    const saved = loadFromStorage(STORAGE_KEYS.FILTERS, null);
    if (saved) {
      return saved;
    }
    return {
      tags: [],
      area: [],
      taluk: [],
      verificationStatus: [],
      status: [],
      priceInfo: "",
      postedType: [],
      createdFrom: "",
      createdTo: "",
      verifiedFrom: "",
      verifiedTo: "",
      postedByBuilder: "",
    };
  };

  const loadInitialTab = () => {
    return loadFromStorage(STORAGE_KEYS.TAB, "pending");
  };

  const loadInitialSearch = () => {
    return loadFromStorage(STORAGE_KEYS.SEARCH, "");
  };

  const loadInitialPage = () => {
    return loadFromStorage(STORAGE_KEYS.PAGE, 1);
  };

  const loadInitialPageSize = () => {
    return loadFromStorage(STORAGE_KEYS.PAGE_SIZE, 10);
  };

  // table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  
  // add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedAdType, setSelectedAdType] = useState("");

  // verify state
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [verifyProperty, setVerifyProperty] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);

  // delete reason
  const [deleteReason, setDeleteReason] = useState("");

  // sold out
  const [isSoldOutModalOpen, setIsSoldOutModalOpen] = useState(false);
  const [soldOutId, setSoldOutId] = useState(null);
  const [soldOutReason, setSoldOutReason] = useState("");
  const [markingSoldOut, setMarkingSoldOut] = useState(false);

  // verify form fields
  const [verifyForm, setVerifyForm] = useState({
    price: "",
    startPrice: "",
    endPrice: "",
    verificationStatus: "",
    tags: [],
    comments: "",
    selectedPlan: null
  });

  // modal forms
  const [modalForm, setModalForm] = useState({
    postingBy: "",
    contactPerson: "",
    //shareOwnerNumber: "",
    //shareOwnerContact: "",
    //shareOwnerEmail: "",
    contactPersonNumber: "",
    contactPersonContact: "",
    //contactPersonEmail: "",
  });

  // edit contact form
  const [editContactForm, setEditContactForm] = useState({
    id: 0,
    postingBy: "",
    contactPerson: "",
    //shareOwnerNumber: "",
    //shareOwnerContact: "",
    //shareOwnerEmail: "",
    contactPersonNumber: "",
    contactPersonContact: "",
    //contactPersonEmail: "",
  });

  // filter state - initialized from sessionStorage
  const [filters, setFilters] = useState(loadInitialFilters);

  // dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    postingBy: [],
    contactPerson: [],
    //shareOwnerNumber: [],
    contactPersonNumber: [],
    verificationStatus: [],
    priceCategory: [],
    tags: [],
    areaOptions: [],
    talukOptions: [],
    postedTypeOptions: [],
    statusOptions: []
  });

  // delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toDeleteId, setToDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // edit contact modal
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [savingContact, setSavingContact] = useState(false);

  // search & pagination - initialized from sessionStorage
  const [searchTerm, setSearchTerm] = useState(loadInitialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(loadInitialSearch);
  const [page, setPage] = useState(loadInitialPage);
  const [pageSize, setPageSize] = useState(loadInitialPageSize);

  // Document state for verify modal
  const [propertyDocuments, setPropertyDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [propertySubscriptions, setPropertySubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionRows, setTransactionRows] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [, setTransactionPropertyUuid] = useState("");
  const [selectedPropertyInfo, setSelectedPropertyInfo] = useState(null);

  // Potential buyers state
  const [potentialBuyersMap, setPotentialBuyersMap] = useState({});
  const [loadingPotentialBuyers, setLoadingPotentialBuyers] = useState(false);
  const [isBuyersModalOpen, setIsBuyersModalOpen] = useState(false);
  const [buyersProperty, setBuyersProperty] = useState(null);
  const [buyersModalError, setBuyersModalError] = useState("");


  // Active tab state - initialized from sessionStorage
  const [activeTab, setActiveTab] = useState(loadInitialTab);
  const [showFilters, setShowFilters] = useState(false);   // ← ADD THIS LINE

  const [tabCounts, setTabCounts] = useState({
    pending: 0,
    reVerification: 0,
    underVerification: 0,
    verified: 0,
    soldOut: 0,
    expired: 0,
  });

  // Dynamic headers based on active tab - Hide verifiedBy and verifiedDate for pending and under_verification tabs
const visibleHeaders = useMemo(() => {
  return headers;
}, []);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.FILTERS, filters);
  }, [filters]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.TAB, activeTab);
  }, [activeTab]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SEARCH, searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.PAGE, page);
  }, [page]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.PAGE_SIZE, pageSize);
  }, [pageSize]);

  // Extract unique values helper
  const extractUniqueValues = (data) => {
    const uniqueAreas = new Set();
    const uniqueTaluks = new Set();
    const uniqueVerificationStatus = new Set();
    const uniqueStatus = new Set();
    const uniquePostedTypes = new Set();

    data.forEach((property) => {
      if (property.raw?.location?.area_name) {
        uniqueAreas.add(property.raw.location.area_name);
      }
      if (property.raw?.location?.taluk_name) {
        uniqueTaluks.add(property.raw.location.taluk_name);
      }
      if (property.verificationStatus) {
        uniqueVerificationStatus.add(property.verificationStatus);
      }
      if (property.status) {
        uniqueStatus.add(property.status);
      }
      if (property.postedType) {
        uniquePostedTypes.add(property.postedType);
      }
    });

    return {
      areas: Array.from(uniqueAreas).map(area => ({ id: area, text: area })),
      taluks: Array.from(uniqueTaluks).map(taluk => ({ id: taluk, text: taluk })),
      verificationStatus: Array.from(uniqueVerificationStatus).map(status => ({ id: status, text: status })),
      status: Array.from(uniqueStatus).map(status => ({ id: status, text: status })),
      postedTypes: Array.from(uniquePostedTypes).map(type => ({ id: type, text: type }))
    };
  };

  // Fetch property documents
  const fetchPropertyDocuments = useCallback(async (propertyUuid) => {
    if (!propertyUuid) return [];
    setLoadingDocuments(true);
    try {
      const response = await callApi("GET", `/property-documents/?property_uuid=${propertyUuid}`);
      if (response?.status_code === 200 && Array.isArray(response.data)) {
        const documents = response.data.map(doc => ({
          id: doc.PROPERTY_DOCUMENT_ID,
          documentId: doc.PROPERTY_DOCUMENT_ID,
          name: doc.DOCUMENT_URL ? doc.DOCUMENT_URL.split('/').pop() : "Document",
          type: doc.ATTRIBUTE_VALUE_NAME || "General",
          category: "Document",
          url: doc.DOCUMENT_URL,
          description: "",
          date: doc.CREATED_TIMESTAMP,
          status: doc.STATUS || "Active",
          attributeId: doc.ATTRIBUTE_ID,
          attributeValueId: doc.ATTRIBUTE_VALUE_ID,
          propertyId: doc.PROPERTY_ID,
          propertyTitle: doc.PROPERTY_TITLE,
          attributeName: doc.ATTRIBUTE_NAME,
          raw: doc,
        }));
        setPropertyDocuments(documents);
        return documents;
      } else {
        setPropertyDocuments([]);
        return [];
      }
    } catch (err) {
      console.error('❌ Failed to fetch documents:', err);
      setPropertyDocuments([]);
      return [];
    } finally {
      setLoadingDocuments(false);
    }
  }, []);

  const fetchDropdownOptions = useCallback(async () => {
    try {
      const response = await callApi("GET", "/attribute-values/");
      const data = Array.isArray(response?.data) ? response.data : [];
      

      const getOptions = (mappingKey) => {
        return data
          .filter(
            (item) =>
              (item.attr_name ||
                item.attribute_name ||
                item.ATTRIBUTE_NAME) === mappingKey
          )
          .map((item) => ({
            id: item.id || item.attribute_value_id || item.ATTRIBUTE_VALUE_ID,
            text: item.attr_val_name || item.attribute_value_name || item.ATTRIBUTE_VALUE_NAME,
          }));
      };

      const options = {
        postingBy: getOptions(ATTR_MAPPING.postingBy),
        contactPerson: getOptions(ATTR_MAPPING.contactPerson),
        //shareOwnerNumber: getOptions(ATTR_MAPPING.shareOwnerNumber),
        contactPersonNumber: getOptions(ATTR_MAPPING.contactPersonNumber),
        verificationStatus: getOptions(ATTR_MAPPING.verificationStatus),
        priceCategory: getOptions(ATTR_MAPPING.measurementUnit),
        tags: getOptions(ATTR_MAPPING.tags).filter(
          (tag) => tag.text?.toLowerCase().trim() !== "special_for_you"
        )
      };

      console.log("Dropdown Options:", options);
      setDropdownOptions((prev) => ({
        ...prev,
        ...options,
      }));
    } catch (err) {
      console.error("Failed to fetch dropdown options:", err);
      setApiError("Failed to load form options");
    }
  }, []);

const fetchSubscriptionPlans = useCallback(async () => {
  try {
    const response = await callApi("GET", "/subscription-plans/");

    const plans = response?.data || [];

    setSubscriptionPlans(
      plans.map((plan) => ({
        id: plan.id,
        name: plan.name?.trim() || "Unnamed Plan",
        code: plan.code,
        planType: plan.plan_type,
        durationDays: plan.durationDays,
        price: plan.price,
        currency: plan.currency,

        // Backend-provided limit information
        propertyCount: plan.propertyCount ?? null,
        propertyLimit: plan.propertyLimit ?? null,
        limitReached: plan.limitReached ?? false,
        message: plan.message || "",

        raw: plan,
      }))
    );
  } catch (error) {
    console.error("Error fetching subscription plans:", error);
  }
}, []);


const fetchPropertySubscriptions = useCallback(async (propertyUuid) => {
  if (!propertyUuid) return [];
  setLoadingSubscriptions(true);
  try {
    const response = await callApi(
      "GET",
      `/property-subscriptions?property_uuid=${propertyUuid}`
    );

    console.log("🔍 property-subscriptions raw response:", response);

    // Accept both { data: [...] } and { data: { items: [...] } }
    const raw =
      (Array.isArray(response?.data) && response.data) ||
      (Array.isArray(response?.data?.items) && response.data.items) ||
      (Array.isArray(response?.items) && response.items) ||
      [];

    const items = raw.map((s) => ({
      id:
        s.id ??
        s.ID ??
        s.PROPERTY_SUBSCRIPTION_ID ??
        s.property_subscription_id,
      subscription_plan_id:
        s.subscription_plan_id ??
        s.SUBSCRIPTION_PLAN_ID,
      plan_name:
        s.plan_name ??
        s.PLAN_NAME,
      plan_code:
        s.plan_code ??
        s.PLAN_CODE,
      amount:
        s.amount ??
        s.AMOUNT,
      currency:
        s.currency ??
        s.CURRENCY ??
        "INR",
   subscription_status:
    s.subscription_status ??
    s.SUBSCRIPTION_STATUS ??
    s.status ??
    s.STATUS,
      property_uuid:
        s.property_uuid ??
        s.PROPERTY_UUID ??
        propertyUuid,
      raw: s,
    }));

    // Filter out already-cancelled rows so they don't show up
// Filter out cancelled rows so they don't show up in the UI
const active = items.filter(
  (s) =>
    !["cancelled", "canceled"].includes(
      (s.subscription_status || s.status || "").toLowerCase()
    )
);

    setPropertySubscriptions(active);
    return active;
  } catch (err) {
    console.error("Failed to fetch property subscriptions:", err);
    setPropertySubscriptions([]);
    return [];
  } finally {
    setLoadingSubscriptions(false);
  }
}, []);

const addPropertySubscription = useCallback(async (propertyId, propertyUuid, plan) => {
  if (!plan) return null;
  const payload = {
    property_id: propertyId,
    property_uuid: propertyUuid,
    subscription_plan_id: plan.id,
    plan_name: plan.name,
    plan_code: plan.code,
    amount: plan.price,
    currency: plan.currency || "INR",
    auto_renewal: false,
  };
  return callApi("POST", "/property-subscriptions", payload);
}, []);


const cancelPropertySubscription = useCallback(async (propertyUuid, subscription) => {
  if (!subscription?.id && !subscription?.subscription_plan_id) return null;
  if (!propertyUuid) throw new Error("property_uuid required");

  const payload = {
    id: subscription.id ?? null,
    property_uuid: propertyUuid,
    subscription_plan_id: subscription.subscription_plan_id,
    plan_name: subscription.plan_name,
    plan_code: subscription.plan_code,
    amount: subscription.amount,
    currency: subscription.currency || "INR",
    auto_renewal: false,
    subscription_status: "CANCELLED",
    updated_by_user_id: 1,
  };

  // 👇 just the raw UUID as a path segment
  const endpoint = `/property-subscriptions/${encodeURIComponent(propertyUuid)}`;

  return callApi("PUT", endpoint, payload);
}, []);



  // Data table fetching
  const fetchProperties = useCallback(async (params = {}) => {
    setLoading(true);
    setApiError("");
    setSuccessMsg("");

    try {
      const query = new URLSearchParams();

      // Support single value OR array for `status`
      if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        statuses.forEach((s) => {
          if (s) query.append("status", s);
        });
      }

      // Support single value OR array for `verification_status`
      if (params.verification_status) {
        const vStatuses = Array.isArray(params.verification_status)
          ? params.verification_status
          : [params.verification_status];
        vStatuses.forEach((v) => {
          if (v) query.append("verification_status", v);
        });
      }

      const endpoint = query.toString()
        ? `/properties/?${query.toString()}`
        : "/properties/";

      const response = await callApi("GET", endpoint);
      const data = Array.isArray(response?.data) ? response.data : [];

const formatted = data.map((p) => {
  const contact = p.contact || {};

  // Contact Person Number gets priority.
  // Owner Number is used only when Contact Person Number is empty.
  const displayNumber =
    contact?.contact_person_number?.toString().trim() ||
    contact?.owner_number?.toString().trim() ||
    "N/P";

  return {
    id: p.id,
    uuid: p.uuid,
    property_uuid: p.uuid,

    propertyTitle: p.title,
    postedType: p.type || "N/P",
    postedFor: p.posted_for || "N/P",

        isBuilderPosted: !!p.builder_profile,

    postedBy: contact?.posted_by || "N/P",
    ownerNumber: displayNumber,

    sequenceID: p.property_seq_id || "N/P",





 
    type: p.type,
    status: p.status,
    description: p.description,
    createdBy: p.created_user || "N/P",
    updatedAt: p.updated_timestamp,
    createdAt: p.created_timestamp,
    priceMeasurementUnit: p.price_measurement_unit,
    startingPrice: p.starting_price,
    endingPrice: p.ending_price,
    tag: p.tag || [],
    paymentStatus: p.payment_status || "N/P",
    verificationStatus: formatVerificationStatus(p.verification_status),
    verifiedBy: p.verified_by_name || "N/P",
    verifiedDate: p.verified_date_time || "N/P",
    verifiedByUserId: p.verified_by_user_id,


    priceInfo: (() => {
      const postedFor = (p.posted_for || "").toLowerCase();

      if (["rent", "pg/hostel", "flatmates"].includes(postedFor)) {
        return p.starting_price
          ? `₹ ${Number(p.starting_price).toLocaleString("en-IN")}`
          : "N/P";
      }

      if (["sale", "resale"].includes(postedFor)) {
        if (p.starting_price && p.ending_price) {
          return `₹ ${Number(p.starting_price).toLocaleString("en-IN")} - ₹ ${Number(p.ending_price).toLocaleString("en-IN")}`;
        }

        if (p.starting_price) {
          return `₹ ${Number(p.starting_price).toLocaleString("en-IN")}`;
        }
      }

      return "N/P";
    })(),

    location: formatLocation(p.location),
    raw: p,
    contact: contact,
    transactionType: p.TRANSACTION_TYPE,
    gateway: p.PAYMENT_GATEWAY,
    currency: p.CURRENCY,
  };
});

      // Remove duplicates
      const uniqueProperties = [];
      const seenIds = new Set();
      const seenUuids = new Set();

      formatted.forEach((property) => {
        let isDuplicate = false;
        if (property.uuid && seenUuids.has(property.uuid)) {
          isDuplicate = true;
        } else if (property.id && seenIds.has(property.id)) {
          isDuplicate = true;
        }

        if (!isDuplicate) {
          if (property.uuid) seenUuids.add(property.uuid);
          if (property.id) seenIds.add(property.id);
          uniqueProperties.push(property);
        }
      });

      if (!mountedRef.current) return;
      setRows(uniqueProperties);

      const filterOptions = extractUniqueValues(uniqueProperties);
      setDropdownOptions(prev => ({
        ...prev,
        areaOptions: filterOptions.areas,
        talukOptions: filterOptions.taluks,
        statusOptions: filterOptions.status,
        postedTypeOptions: filterOptions.postedTypes
      }));
    } catch (err) {
      console.error("fetchProperties error:", err);
      if (!mountedRef.current) return;
      setApiError("Unable to load properties. Please try again.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const fetchTabCounts = useCallback(async () => {
    try {
      const [
        pendingRes,
        reVerificationRes,
        underVerificationRes,
        verifiedRes,
        soldOutRes,
        expiredRes,
      ] = await Promise.all([
        callApi("GET", "/properties/?verification_status=pending"),
        callApi("GET", "/properties/?verification_status=re-verification"),
        callApi(
  "GET",
  `/properties/?${UNDER_VERIFICATION_PARAMS.map(
    (v) => `verification_status=${encodeURIComponent(v)}`
  ).join("&")}`
),
        callApi("GET", "/properties/?verification_status=verified"),
        callApi("GET", "/properties/?status=soldout"),
        callApi("GET", "/properties/?status=expired"),
      ]);

      const getData = (response) => {
        return Array.isArray(response?.data) ? response.data : [];
      };

      const pendingData = getData(pendingRes);
      const reVerificationData = getData(reVerificationRes);
      const underVerificationData = getData(underVerificationRes);
      const verifiedData = getData(verifiedRes);
      const soldOutData = getData(soldOutRes);
      const expiredData = getData(expiredRes);

      const normalize = (value) => (value || "").toLowerCase().trim();

      setTabCounts({
        pending: pendingData.filter((property) =>
          ["pending", "draft", "pending for verification"].includes(
            normalize(property.verification_status)
          )
        ).length,
        reVerification: reVerificationData.filter((property) =>
          ["re_verification", "re verification", "re-verification"].includes(
            normalize(property.verification_status)
          )
        ).length,
underVerification: underVerificationData.filter((property) =>
  isUnderVerification(property.verification_status)
).length,
        verified: verifiedData.filter(
          (property) =>
            normalize(property.verification_status) === "verified" &&
            normalize(property.status) !== "soldout"
        ).length,
        soldOut: soldOutData.filter(
          (property) => normalize(property.status) === "soldout"
        ).length,
        expired: expiredData.filter(
          (property) => normalize(property.status) === "expired"
        ).length,
      });
    } catch (error) {
      console.error("Failed to fetch property tab counts:", error);
    }
  }, []);

// Fetch potential buyers for a single property (on demand)
// Fetch potential buyers for a single property (on demand)
const fetchPotentialBuyersForProperty = useCallback(async (propertyUuid) => {
  if (!propertyUuid) return { buyers: [], total: 0 };

  setLoadingPotentialBuyers(true);
  setBuyersModalError("");

  try {
    const response = await callMongoApi(
      "GET",
      `/property-leads/property/${encodeURIComponent(propertyUuid)}`
    );

    // Safely unwrap the response data
    const payload =
      response?.data?.data ??   // if interceptor returns { data: { data: {...} } }
      response?.data ??         // if interceptor returns { data: {...} }
      response ??               // if already fully unwrapped
      {};

    // The API returns an array of properties. We take the first one matching our UUID.
    const propertyData = 
      (Array.isArray(payload.properties) && payload.properties.length > 0)
        ? payload.properties[0] 
        : payload;

    // Extract buyers and total from the propertyData object
    const buyers = propertyData?.potentialBuyers || [];
    const total = propertyData?.total ?? buyers.length;

    // Cache in the map
    setPotentialBuyersMap((prev) => ({
      ...prev,
      [propertyUuid]: { buyers, total },
    }));

    return { buyers, total };
  } catch (err) {
    console.error("Failed to fetch potential buyers:", err);
    setBuyersModalError(
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load potential buyers."
    );
    return { buyers: [], total: 0 };
  } finally {
    setLoadingPotentialBuyers(false);
  }
}, []);

  const formatLocation = (loc) => {
    if (!loc || typeof loc !== "object") return "-";
    const parts = [
      loc.landmark,
      loc.address_line_1,
      loc.address_line_2,
      //loc.area_name,
      loc.village_name,
      loc.taluk_name,
      loc.district_name,
      loc.state_name,
      loc.country_name,
      loc.pin_code
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  };

  // Handle tab change
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setPage(1);
  };

  // Fetch properties based on active tab
  // Fetch properties based on active tab
useEffect(() => {
  if (activeTab === "pending") {
    fetchProperties({ verification_status: "pending" });   // backend only returns Pending
  } else if (activeTab === "re-verification") {
      fetchProperties({ verification_status: "re-verification" });
    } else if (activeTab === "under_verification") {
      fetchProperties({
        verification_status: UNDER_VERIFICATION_PARAMS
      });
    } else if (activeTab === "verified") {
      fetchProperties({ verification_status: "verified" });
    } else if (activeTab === "soldout") {
      fetchProperties({ status: "soldout" });
    } else if (activeTab === "expired") {
      fetchProperties({ status: "expired" });
    }
  }, [activeTab, fetchProperties]);
  const fetchTransactionsByProperty = async (propertyUuid) => {
    console.log("Fetching transactions for property UUID:", propertyUuid);
    setTransactionRows([]);
    setIsTransactionModalOpen(true);
    setLoadingTransactions(true);
    setApiError("");

    try {
      const response = await callApi("GET", `/transactions/property/${propertyUuid}`);
      console.log("Transactions API Response:", response);

      const transactionsData = response?.data;
      if (!transactionsData) {
        console.log("No transactions data in response");
        setTransactionRows([]);
        return;
      }

      let transactionsArray = [];
      if (Array.isArray(transactionsData)) {
        transactionsArray = transactionsData;
      } else if (transactionsData && typeof transactionsData === 'object') {
        transactionsArray = [transactionsData];
      }

      if (transactionsArray.length === 0) {
        console.log("No transactions found for this property");
        setTransactionRows([]);
        return;
      }

      console.log("Transactions array:", transactionsArray);

      const formattedTransactions = transactionsArray.map(t => ({
        id: String(t.PROPERTY_TRANSACTION_ID || t.id || Math.random()),
        transactionId: t.PROPERTY_TRANSACTION_ID || t.id || "-",
        propertyUuid: t.PROPERTY_UUID || propertyUuid,
        userId: t.USER_ID || "-",
        name: t.name || "N/A",
        paymentId: t.FK_PROPERTY_PAYMENT_ID || t.payment_id || "-",
        planName: t.AMOUNT_BREAKUP?.planName?.trim() || "-",
        transactionType: t.TRANSACTION_TYPE || "-",
        gstPercentage: t.AMOUNT_BREAKUP?.gstPercentage !== undefined &&
          t.AMOUNT_BREAKUP?.gstPercentage !== null
          ? `${t.AMOUNT_BREAKUP.gstPercentage}%`
          : "-",
        amount: t.AMOUNT ? `₹ ${Number(t.AMOUNT).toLocaleString('en-IN')}` : "-",
        currency: t.CURRENCY || "INR",
        paymentMethod: t.PAYMENT_METHOD || "-",
        paymentGateway: t.PAYMENT_GATEWAY || "-",
        gatewayTxnId: t.GATEWAY_TRANSACTION_ID || "-",
        invoiceNumber: t.invoice_number || "-",
        invoicePdfPath: t.invoice_pdf_path || null,
        status: t.STATUS || "-",
        description: t.DESCRIPTION || "-",
        createdAt: t.CREATED_AT || "",
        createdDate: t.CREATED_AT || "",
        raw: t
      }));
      console.log("Formatted transactions:", formattedTransactions);
      setTransactionRows(formattedTransactions);
    } catch (err) {
      console.error("Transaction fetch error:", err);
      setApiError(`Failed to load transactions: ${err?.response?.data?.message || err.message}`);
      setTransactionRows([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchPropertyContact = useCallback(async (propertyUuid) => {
    if (!propertyUuid) return;
    try {
      const response = await callApi("GET", `/property-contacts/?property_uuid=${propertyUuid}`);
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const contactData = response.data[0];
        setEditContactForm({
          id: contactData.contact_id || contactData.PROPERTY_CONTACTS_ID || 0,
          postingBy: contactData.posted_by || contactData.owner_email || "",
          contactPerson: contactData.contact_person || "",
          //shareOwnerNumber: contactData.owner_number ? "Yes" : "No",
          //shareOwnerContact: contactData.owner_number || "",
          //shareOwnerEmail: contactData.owner_email || "",
          contactPersonNumber: contactData.contact_number ? "Yes" : "No",
          contactPersonContact: contactData.contact_number || "",
          //contactPersonEmail: contactData.contact_email || "",
        });
      } else {
        setEditContactForm({
          id: 0,
          postingBy: "",
          contactPerson: "",
          //shareOwnerNumber: "",
          //shareOwnerContact: "",
          //shareOwnerEmail: "",
          contactPersonNumber: "",
          contactPersonContact: "",
          //contactPersonEmail: "",
        });
      }
    } catch (err) {
      console.error("Failed to fetch contact data:", err);
      setEditContactForm({
        id: 0,
        postingBy: "",
        contactPerson: "",
        //shareOwnerNumber: "",
        //shareOwnerContact: "",
        //shareOwnerEmail: "",
        contactPersonNumber: "",
        contactPersonContact: "",
        //contactPersonEmail: "",
      });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchDropdownOptions();
    fetchTabCounts();
    fetchSubscriptionPlans();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchDropdownOptions, fetchTabCounts, fetchSubscriptionPlans]);

  const handleModalChange = (field, value) => {
    setModalForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditContactChange = (field, value) => {
    setEditContactForm((prev) => ({ ...prev, [field]: value }));
  };

const statusOptionsByTab = useMemo(() => {
  if (activeTab === "verified") {
    return [
      { id: "active", text: "Active" },
      { id: "sold", text: "Sold Out" },
      { id: "cancelled", text: "Cancelled" },
    ];
  }
  if (activeTab === "pending") {
    return [
      { id: "active", text: "Active" },
      { id: "cancelled", text: "Cancelled" },
    ];
  }
  if (activeTab === "under_verification") {
    return [
      { id: "active", text: "Active" },
      { id: "cancelled", text: "Cancelled" },
    ];
  }
  if (activeTab === "re-verification") {
    return [
      { id: "active", text: "Active" },
      { id: "cancelled", text: "Cancelled" },
    ];
  }
  return [];
}, [activeTab]);

  const verificationOptionsByTab = useMemo(() => {
    if (!dropdownOptions.verificationStatus.length) return [];
    return dropdownOptions.verificationStatus.filter(option => {
      const value = option.text?.toLowerCase().trim();
      if (activeTab === "pending") {
        return [
          "pending",
          "draft",
          "pending for verification",
          "under_verification",
          "under verification"
        ].includes(value);
      }
      if (activeTab === "verified") {
        return value === "verified";
      }
      return true;
    });
  }, [dropdownOptions.verificationStatus, activeTab]);

  // Filter functions
const handleFilterChange = (filterName, value) => {
  const newFilters = {
    ...filters,
    [filterName]: value,
  };

  setFilters(newFilters);
  setPage(1);

  // =====================================================
  // 1. VERIFICATION STATUS FILTER
  // =====================================================
  if (filterName === "verificationStatus") {

    // If Draft is selected
    if (value.length > 0) {
      const selectedVerificationStatus = value[0].text
        .toLowerCase()
        .trim();

      // Draft
      if (selectedVerificationStatus === "draft") {
        fetchProperties({
          verification_status: "draft",
        });
        return;
      }

      // Pending
      if (selectedVerificationStatus === "pending") {
        fetchProperties({
          verification_status: "pending",
        });
        return;
      }

      // Pending for Verification
      if (
        selectedVerificationStatus === "pending for verification"
      ) {
        fetchProperties({
          verification_status: "pending for verification",
        });
        return;
      }

      // Other verification statuses
      fetchProperties({
        verification_status: selectedVerificationStatus,
      });

      return;
    }

    // Verification Status filter cleared
    if (value.length === 0) {
      if (activeTab === "pending") {
        fetchProperties({
          verification_status: [
            "pending",
            "draft",
            "pending for verification",
          ],
        });
      } else if (activeTab === "re-verification") {
        fetchProperties({
          verification_status: "re-verification",
        });
      } else if (activeTab === "under_verification") {
        fetchProperties({
          verification_status: UNDER_VERIFICATION_PARAMS,
        });
      } else if (activeTab === "verified") {
        fetchProperties({
          verification_status: "verified",
        });
      } else if (activeTab === "soldout") {
        fetchProperties({
          status: "soldout",
        });
      } else if (activeTab === "expired") {
        fetchProperties({
          status: "expired",
        });
      }

      return;
    }
  }

  // =====================================================
  // 2. PROPERTY STATUS FILTER
  // =====================================================
  if (filterName === "status" && value.length > 0) {
    const selectedStatus = value[0].text
      .toLowerCase()
      .trim();

    const params = {
      status: selectedStatus,
    };

    if (activeTab === "pending") {
      params.verification_status = "pending";
    } else if (activeTab === "re-verification") {
      params.verification_status = "re-verification";
    } else if (activeTab === "under_verification") {
      params.verification_status = [
        "under_verification",
        "Under Verification",
      ];
    } else if (activeTab === "verified") {
      params.verification_status = "verified";
    } else if (activeTab === "soldout") {
      params.status = "soldout";
    } else if (activeTab === "expired") {
      params.status = "expired";
    }

    fetchProperties(params);
    return;
  }

  // =====================================================
  // 3. PROPERTY STATUS FILTER CLEARED
  // =====================================================
  if (filterName === "status" && value.length === 0) {
    if (activeTab === "pending") {
      fetchProperties({
        verification_status: [
          "pending",
          "draft",
          "pending for verification",
        ],
      });
    } else if (activeTab === "re-verification") {
      fetchProperties({
        verification_status: "re-verification",
      });
    } else if (activeTab === "under_verification") {
      fetchProperties({
        verification_status: UNDER_VERIFICATION_PARAMS,
      });
    } else if (activeTab === "verified") {
      fetchProperties({
        verification_status: "verified",
      });
    } else if (activeTab === "soldout") {
      fetchProperties({
        status: "soldout",
      });
    } else if (activeTab === "expired") {
      fetchProperties({
        status: "expired",
      });
    }

    return;
  }
};



  // Apply filters function
  const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) return data;

    return data.filter(row => {
      // Filter by tags
      if (filters.tags.length > 0) {
        const rowTags = Array.isArray(row.tag) ? row.tag : [row.tag].filter(Boolean);
        const hasMatchingTag = filters.tags.some(filterTag =>
          rowTags.some(rowTag => rowTag === filterTag.text)
        );
        if (!hasMatchingTag) return false;
      }

      // Filter by area
      if (filters.area.length > 0) {
        const rowArea = row.raw?.location?.area_name || "";
        const hasMatchingArea = filters.area.some(filterArea =>
          filterArea.text === rowArea
        );
        if (!hasMatchingArea) return false;
      }

      // Filter by taluk
      if (filters.taluk.length > 0) {
        const rowTaluk = row.raw?.location?.taluk_name || "";
        const hasMatchingTaluk = filters.taluk.some(filterTaluk =>
          filterTaluk.text === rowTaluk
        );
        if (!hasMatchingTaluk) return false;
      }

      // Filter by posted type
      if (filters.postedType.length > 0) {
        const rowPostedType = row.postedType || "";
        const hasMatchingPostedType = filters.postedType.some(filterPostedType =>
          filterPostedType.text === rowPostedType
        );
        if (!hasMatchingPostedType) return false;
      }

      // Filter by verification status
      if (filters.verificationStatus.length > 0) {
        const rowStatus = (row.verificationStatus || "").toLowerCase().trim();
        const matches = filters.verificationStatus.some((item) => {
          const selected = item.text.toLowerCase().trim();
          if (selected === "pending") {
            return [
              "pending",
              "draft",
              "pending for verification",
              "under_verification",
              "under verification"
            ].includes(rowStatus);
          }
          if (selected === "verified") {
            return rowStatus === "verified";
          }
          if (selected === "draft") {
            return rowStatus === "draft";
          }
          return rowStatus === selected;
        });
        if (!matches) return false;
      }

      // Filter by status
      if (filters.status.length > 0) {
        const normalize = (val) => (val || "").toLowerCase().replace(/\s+/g, "");
        const rowStatus = normalize(row.status);
        const hasMatchingStatus = filters.status.some(
          s => normalize(s.text) === rowStatus
        );
        if (!hasMatchingStatus) return false;
      }

      if (filters.createdFrom || filters.createdTo) {
        const createdDate = new Date(row.createdAt);
        if (filters.createdFrom) {
          const from = new Date(filters.createdFrom);
          if (createdDate < from) return false;
        }
        if (filters.createdTo) {
          const to = new Date(filters.createdTo);
          to.setHours(23, 59, 59, 999);
          if (createdDate > to) return false;
        }
      }

      if (filters.verifiedFrom || filters.verifiedTo) {
        if (!row.verifiedDate) return false;
        const verifiedDate = new Date(row.verifiedDate);
        if (filters.verifiedFrom) {
          const from = new Date(filters.verifiedFrom);
          if (verifiedDate < from) return false;
        }
        if (filters.verifiedTo) {
          const to = new Date(filters.verifiedTo);
          to.setHours(23, 59, 59, 999);
          if (verifiedDate > to) return false;
        }
      }

      // Filter by price info
      if (filters.priceInfo) {
        const priceStr = row.priceInfo?.toString().toLowerCase() || "";
        if (!priceStr.includes(filters.priceInfo.toLowerCase())) {
          return false;
        }
      }

            // Filter by Posted By Builder
      if (filters.postedByBuilder) {
        const isBuilder = filters.postedByBuilder === "yes";
        if (row.isBuilderPosted !== isBuilder) return false;
      }

      return true;
    });
  }, [filters]);

  // Clear all filters - THIS ONLY CLEARS WHEN BUTTON IS CLICKED
  const clearAllFilters = () => {
    const emptyFilters = {
      tags: [],
      area: [],
      taluk: [],
      verificationStatus: [],
      priceInfo: "",
      status: [],
      postedType: [],
      createdFrom: "",
      createdTo: "",
      verifiedFrom: "",
      verifiedTo: "",
      postedByBuilder: "",
    };
    
    setFilters(emptyFilters);
    // Also clear search when clearing filters
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
  };

  // IMPORTANT: Do NOT auto-clear filters on tab change
  // Remove the useEffect that clears filters on tab change

  const handleStartPosting = () => {
    const contactData = {
      postingBy: modalForm.postingBy,
      contactPerson: modalForm.contactPerson,
      //shareOwnerNumber: modalForm.shareOwnerNumber,
      //shareOwnerContact: modalForm.shareOwnerContact,
      //shareOwnerEmail: modalForm.shareOwnerEmail,
      contactPersonNumber: modalForm.contactPersonNumber,
      contactPersonContact: modalForm.contactPersonContact,
      //contactPersonEmail: modalForm.contactPersonEmail,
    };

    const query = `?type=${encodeURIComponent(selectedType)}&ad=${encodeURIComponent(selectedAdType)}`;
    setIsAddModalOpen(false);

    navigate(`/settings/property-management/add${query}`, {
      state: {
        contactData,
        propertyType: selectedType,
        adType: selectedAdType,
      },
    });
  };

  const handleEditProperty = (property) => {
    const raw = property.raw || {};
    const propertyUuid = property.uuid || property.property_uuid || raw.uuid || raw.PROPERTY_UUID || raw.id || null;

    let propertyType = property.type || raw.type || raw.PROPERTY_TYPE || "";
    let postedFor = property.postedFor || raw.posted_for || raw.PROPERTY_POSTED_FOR || "";

    const pf = postedFor.toString().trim().toLowerCase();
    if (pf === "rent") postedFor = "Rent";
    else if (pf === "resale") postedFor = "Resale";
    else if (pf === "pg" || pf === "pg/hostel") postedFor = "PG/Hostel";
    else if (pf === "flatmates") postedFor = "Flatmates";
    else if (pf === "sale") postedFor = "Sale";

    if (!propertyUuid || !postedFor || !propertyType) {
      setApiError(`Cannot edit property. Missing required data.`);
      return;
    }

    if (!PROPERTY_TYPES[propertyType]) {
      setApiError(`Invalid property type: ${propertyType}`);
      return;
    }

    if (!PROPERTY_TYPES[propertyType].includes(postedFor)) {
      setApiError(`Invalid combination: "${postedFor}" is not valid for "${propertyType}" properties.`);
      return;
    }

    const query = `?type=${encodeURIComponent(propertyType)}&ad=${encodeURIComponent(postedFor)}&property_uuid=${encodeURIComponent(propertyUuid)}&form_type=edit`;

    navigate(`/settings/property-management/add${query}`);
  };

   const handleVerify = async (property) => {
    setApiError("");
    const rawProperty = property.raw || {};
    const isSale = property.postedFor === "sale" || property.postedFor === "resale";
    const isRent = property.postedFor === "rent" || property.postedFor === "pg/hostel" || property.postedFor === "flatmates";

    let price = "";
    let startPrice = "";
    let endPrice = "";

    if (isSale) {
      startPrice = rawProperty.starting_price?.toString() || "";
      endPrice = rawProperty.ending_price?.toString() || "";
    } else if (isRent) {
      price = rawProperty.starting_price?.toString() || "";
    }

    let propertyTags = [];
    if (Array.isArray(property.tag)) {
      propertyTags = property.tag;
    } else if (property.tag) {
      propertyTags = [property.tag];
    }

const normalizeText = (t) => (t || "").toString().trim().toLowerCase();

const matchingTags = dropdownOptions.tags.filter((tagOption) =>
  propertyTags.some(
    (tag) => normalizeText(tag) === normalizeText(tagOption.text)
  )
); 
    const propertyUuid = property.uuid || property.property_uuid;

    // Check if this property already has plan(s) attached via property-subscriptions
    const existingSubscriptions = propertyUuid
      ? await fetchPropertySubscriptions(propertyUuid)
      : [];

    const existingPlanIds = existingSubscriptions
      .map((s) => s.subscription_plan_id)
      .filter((id) => id !== undefined && id !== null);

    const existingPlanNames = existingSubscriptions
      .map((s) => s.plan_name?.trim())
      .filter(Boolean);

    setVerifyForm({
      price: price,
      startPrice: startPrice,
      endPrice: endPrice,
      verificationStatus: property.verificationStatus || rawProperty.verification_status || "",
      tags: matchingTags,
      comments: rawProperty.comments || rawProperty.verification_comments || "",
      selectedPlan: null
    });

    setVerifyProperty({
      ...property,
      existingSubscriptions,
      existingPlanIds,
      existingPlanNames,
      hasExistingPlan: existingSubscriptions.length > 0
    });

    if (propertyUuid) {
      await fetchPropertyDocuments(propertyUuid);
    }

    setIsVerifyModalOpen(true);
  };

  const handleDownloadDocument = (documentUrl) => {
    if (!documentUrl) return;
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = documentUrl.split('/').pop() || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDocument = (documentUrl) => {
    if (!documentUrl) return;
    window.open(documentUrl, '_blank');
  };

  const handleEditContact = async (property) => {
    setEditingProperty(property);
    setIsEditContactModalOpen(true);
    setApiError("");

    const propertyUuid = property.uuid || property.property_uuid;
    if (propertyUuid) {
      await fetchPropertyContact(propertyUuid);
    } else {
      setApiError("Cannot edit contact: Missing property identifier");
    }
  };

  const isValidEmail = (email) => {
    if (!email) return true;
    const trimmed = email.trim().toLowerCase();
    return /^[a-z0-9._%+-]+@gmail\.com$/.test(trimmed);
  };

  const handleUpdateContact = async () => {
    if (!editingProperty) return;
    if (editContactForm.shareOwnerEmail && !isValidEmail(editContactForm.shareOwnerEmail)) {
      setApiError("Owner email must contain @ and be valid.");
      return;
    }
    if (editContactForm.contactPersonEmail && !isValidEmail(editContactForm.contactPersonEmail)) {
      setApiError("Contact person email must contain @ and be valid.");
      return;
    }

    setSavingContact(true);
    setApiError("");

    try {
      const propertyUuid = editingProperty.uuid || editingProperty.property_uuid;
      if (!propertyUuid) throw new Error("Missing property UUID");

      const contactPayload = {
        property_uuid: propertyUuid,
        contact: {
          PROPERTY_CONTACTS_ID: editContactForm.id || 0,
          PROPERTY_POSTED_BY: editContactForm.postingBy || "",
          PROPERTY_OWNER_NUMBER: editContactForm.shareOwnerNumber === "Yes" ? editContactForm.shareOwnerContact : "",
          PROPERTY_OWNER_EMAIL: editContactForm.shareOwnerNumber === "Yes" ? editContactForm.shareOwnerEmail : "",
          PROPERTY_CONTACT_PERSON: editContactForm.contactPerson || "",
          PROPERTY_CONTACT_PERSON_NUMBER: editContactForm.contactPersonNumber === "Yes" ? editContactForm.contactPersonContact : "",
          PROPERTY_CONTACT_PERSON_EMAIL: editContactForm.contactPersonNumber === "Yes" ? editContactForm.contactPersonEmail : "",
          UPDATED_BY_USER_ID: 1,
        }
      };

      const endpoint = `/property-contacts/?property_uuid=${propertyUuid}`;
      const method = editContactForm.id > 0 ? "PUT" : "POST";

      await callApi(method, endpoint, contactPayload);
      setSuccessMsg("Contact information updated successfully.");
      setIsEditContactModalOpen(false);
setEditingProperty(null);

await fetchProperties();
    } catch (err) {
      console.error("Failed to update contact:", err);
      setApiError("Failed to update contact information. Please try again.");
    } finally {
      setSavingContact(false);
    }
  };

const handleRemovePlan = async (subscription) => {
  if (!verifyProperty?.uuid) return;

  setApiError("");
  setSuccessMsg("");

  const removedKey = String(
    subscription.id ?? subscription.subscription_plan_id
  );

  try {
    // 1. Cancel subscription — backend also removes the auto-added tag
    await cancelPropertySubscription(verifyProperty.uuid, subscription);

    // 2. Determine the tag that this plan had auto-added so we can
    //    strip it from verifyForm.tags (local UI state only)
    const rawTag =
      subscription.tag ||
      subscription.plan_code ||
      subscription.plan_name;

    const normalizeTag = (v) =>
      String(v || "").toLowerCase().trim().replace(/\s+/g, "_");

    const tagToRemove = rawTag ? normalizeTag(rawTag) : null;

    console.log("🗑️ Plan removed → tag to strip locally:", tagToRemove);
    console.log("   verifyForm.tags before:", verifyForm.tags);

    // 3. Remove that tag from the MultiSelect source (verifyForm.tags)
    if (tagToRemove) {
      setVerifyForm((prev) => {
        const filtered = (prev.tags || []).filter((t) => {
          const text = typeof t === "string" ? t : t.text;
          return normalizeTag(text) !== tagToRemove;
        });
        console.log("   verifyForm.tags after:", filtered);
        return { ...prev, tags: filtered };
      });
    }

    // 4. Remove subscription from local state (dropdown updates instantly)
    setVerifyProperty((prev) => {
      if (!prev) return prev;

      const remaining = (prev.existingSubscriptions || []).filter(
        (s) =>
          String(s.id ?? s.subscription_plan_id) !== removedKey
      );

      return {
        ...prev,
        existingSubscriptions: remaining,
        existingPlanIds: remaining
          .map((s) => s.subscription_plan_id)
          .filter((id) => id !== undefined && id !== null),
        existingPlanNames: remaining
          .map((s) => s.plan_name?.trim())
          .filter(Boolean),
        hasExistingPlan: remaining.length > 0,
      };
    });

    setSuccessMsg("Subscription plan removed successfully.");
  } catch (err) {
    console.error("Failed to remove plan:", err);
    setApiError(
      err?.response?.data?.message ||
      err?.message ||
      "Failed to remove subscription plan."
    );
  }
};

const handleVerifyConfirmed = async () => {
  if (!verifyProperty) return;

  setVerifyingId(verifyProperty.id);
  setApiError("");
  setSuccessMsg("");

  try {
    const postedFor = (verifyProperty?.postedFor || "").toLowerCase();

    const isSale = postedFor === "sale" || postedFor === "resale";
    const isRent =
      postedFor === "rent" ||
      postedFor === "pg/hostel" ||
      postedFor === "flatmates";

    // --------------------------------------------------
    // 0. ✅ PRICE VALIDATION — block if missing
    // --------------------------------------------------
    if (isSale) {
      if (!verifyForm.startPrice || Number(verifyForm.startPrice) <= 0) {
        setApiError("Price is required to verify this property.");
        setVerifyingId(null);
        return;
      }
    }

    if (isRent) {
      if (!verifyForm.price || Number(verifyForm.price) <= 0) {
        setApiError("Rental amount is required to verify this property.");
        setVerifyingId(null);
        return;
      }
    }

    const tagTexts = verifyForm.tags.map((tag) => tag.text);

    // ✅ Snapshot the selected plan BEFORE mutating state
    const selectedPlan = verifyForm.selectedPlan;

    const isNewPlanSelected =
      selectedPlan &&
      !(verifyProperty.existingPlanIds || []).includes(selectedPlan.id);

    // --------------------------------------------------
    // 1. ADD SUBSCRIPTION PLAN (if selected)
    // --------------------------------------------------
    if (isNewPlanSelected) {
      try {
        await addPropertySubscription(
          verifyProperty.id,
          verifyProperty.uuid,
          selectedPlan
        );

        // Check plan limit AFTER adding
        if (
          selectedPlan?.limitReached === true &&
          selectedPlan?.message
        ) {
          setApiError(selectedPlan.message);
          setVerifyingId(null);
          return; // keep modal open
        }

        // Optimistically update local state
        setVerifyProperty((prev) => {
          if (!prev) return prev;

          const newSubscription = {
            id: null,
            subscription_plan_id: selectedPlan.id,
            plan_name: selectedPlan.name,
            plan_code: selectedPlan.code,
            amount: selectedPlan.price,
            currency: selectedPlan.currency || "INR",
            subscription_status: "ACTIVE",
          };

          const merged = [
            ...(prev.existingSubscriptions || []),
            newSubscription,
          ];

          return {
            ...prev,
            existingSubscriptions: merged,
            existingPlanIds: merged
              .map((s) => s.subscription_plan_id)
              .filter((id) => id !== undefined && id !== null),
            existingPlanNames: merged
              .map((s) => s.plan_name?.trim())
              .filter(Boolean),
            hasExistingPlan: true,
          };
        });

        // ❌ DO NOT return here — continue to property verification
      } catch (err) {
        console.error("Failed to add subscription plan:", err);
        setApiError(
          err?.message ||
          err?.responseData?.message ||
          err?.responseData?.detail ||
          "Failed to add subscription plan."
        );
        setVerifyingId(null);
        return; // abort — keep modal open
      }
    }

    // --------------------------------------------------
    // 2. PREPARE PROPERTY UPDATE PAYLOAD
    // ✅ Use the LOCAL selectedPlan variable, not verifyForm
    // --------------------------------------------------
    const payload = {
      PROPERTY_ID: verifyProperty.id,
      PROPERTY_UUID: verifyProperty.uuid,
      PROPERTY_POSTED_FOR: verifyProperty?.postedFor,
      TAG: tagTexts,
      VERIFICATION_STATUS: normalizeVerificationStatus(
        verifyForm.verificationStatus
      ),
      UPDATED_BY_USER_ID: 1,
      COMMENTS: verifyForm.comments || "",
      SUBSCRIPTION_PLAN_ID: selectedPlan?.id || null,
      SUBSCRIPTION_PLAN_NAME: selectedPlan?.name || null,
    };

    if (isSale) {
      payload.STARTING_PRICE = verifyForm.startPrice
        ? Number(verifyForm.startPrice)
        : null;
      payload.ENDING_PRICE = verifyForm.endPrice
        ? Number(verifyForm.endPrice)
        : null;
    }

    if (isRent) {
      payload.STARTING_PRICE = verifyForm.price
        ? Number(verifyForm.price)
        : null;
      payload.ENDING_PRICE = null;
    }

    // --------------------------------------------------
    // 3. UPDATE PROPERTY (verify)
    // --------------------------------------------------
    console.log("🚀 PUT /properties/ payload:", JSON.stringify(payload, null, 2));
    const res = await callApi("PUT", "/properties/", payload);
    console.log("✅ PUT /properties/ response:", res);
    // --------------------------------------------------
    // 4. CLOSE MODAL + RESET STATE
    // --------------------------------------------------
    setIsVerifyModalOpen(false);
    setVerifyProperty(null);

    setVerifyForm({
      price: "",
      startPrice: "",
      endPrice: "",
      verificationStatus: "",
      tags: [],
      comments: "",
      selectedPlan: null,
    });

    setSuccessMsg(
      isNewPlanSelected
        ? "Plan added and property verified successfully."
        : "Property verified successfully."
    );

    // --------------------------------------------------
    // 5. REFRESH COUNTS
    // --------------------------------------------------
    await fetchTabCounts();

    // --------------------------------------------------
    // 6. REFRESH CURRENT TAB
    // --------------------------------------------------
    if (activeTab === "pending") {
      await fetchProperties({ verification_status: "pending" });
    } else if (activeTab === "re-verification") {
      await fetchProperties({ verification_status: "re-verification" });
    } else if (activeTab === "under_verification") {
      await fetchProperties({ verification_status: UNDER_VERIFICATION_PARAMS });
    } else if (activeTab === "verified") {
      await fetchProperties({ verification_status: "verified" });
    } else if (activeTab === "soldout") {
      await fetchProperties({ status: "soldout" });
    } else if (activeTab === "expired") {
      await fetchProperties({ status: "expired" });
    }
  } catch (err) {
    console.error("Verify error:", err);
    setApiError(
      err?.response?.data?.message ||
      "Failed to verify property. Please try again."
    );
  } finally {
    setVerifyingId(null);
  }
};
  const handleSoldOutConfirmed = async () => {
    if (!soldOutId || !soldOutReason.trim()) return;
    setMarkingSoldOut(true);
    setApiError("");
    setSuccessMsg("");

    try {
      const payload = {
        PROPERTY_ID: soldOutId,
        STATUS: "USER_STATUS_SOLD",
        STATUS_CHANGE_COMMENT: soldOutReason,
      };
      await callApi("POST", "/properties/status", payload);
      setSuccessMsg("Property marked as Sold Out successfully.");
      setIsSoldOutModalOpen(false);
      setSoldOutId(null);
      setSoldOutReason("");
      await fetchProperties();
      await fetchTabCounts();
    } catch (err) {
      console.error("Sold Out error:", err);
      setApiError("Failed to mark property as Sold Out.");
    } finally {
      setMarkingSoldOut(false);
    }
  };

  const confirmDelete = (id) => {
    setToDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!toDeleteId || !deleteReason.trim()) return;
    setDeletingId(toDeleteId);
    setApiError("");
    setSuccessMsg("");

    try {
      const payload = {
        PROPERTY_ID: toDeleteId,
        STATUS: "Cancelled",
        CANCELLATION_REASON: deleteReason,
        UPDATED_BY_USER_ID: 1,
      };
      await callApi("POST", "/properties/status", payload);
      setSuccessMsg("Property cancelled successfully.");
      setIsDeleteModalOpen(false);
      setToDeleteId(null);
      setDeleteReason("");
      await fetchProperties();
      await fetchTabCounts();
    } catch (err) {
      console.error("Cancel property error:", err);
      setApiError("Failed to cancel property. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  const isContactFormValid = useCallback(() => {
    if (!modalForm.postingBy) return false;
    if (modalForm.shareOwnerNumber === "Yes") {
      const ownerNumber = (modalForm.shareOwnerContact || "").trim();
      if (!/^\d{10}$/.test(ownerNumber)) return false;
      if (modalForm.shareOwnerEmail && !isValidEmail(modalForm.shareOwnerEmail)) return false;
    }
    if (modalForm.contactPersonNumber === "Yes") {
      const contactNumber = (modalForm.contactPersonContact || "").trim();
      if (!/^\d{10}$/.test(contactNumber)) return false;
      if (modalForm.contactPersonEmail && !isValidEmail(modalForm.contactPersonEmail)) return false;
    }
    return true;
  }, [modalForm]);

  const validateNumericInput = (value) => {
    return value.replace(/[^0-9]/g, "");
  };

  const findSelectedItem = (options, value) => {
    return options.find((item) => item.text === value) || null;
  };

  const handleEditTitle = (property) => {
    setEditingTitleId(property.id);
    setEditingTitle(property.propertyTitle || "");
    setApiError("");
  };

  const handleCancelTitleEdit = () => {
    setEditingTitleId(null);
    setEditingTitle("");
  };

  const handleSaveTitle = async (property) => {
    const newTitle = editingTitle.trim();
    if (!newTitle) {
      setApiError("Property title cannot be empty.");
      return;
    }

    const propertyUuid = property.uuid || property.property_uuid || property.raw?.uuid;
    if (!propertyUuid) {
      setApiError("Cannot update title. Property UUID is missing.");
      return;
    }

    setSavingTitle(true);
    setApiError("");

    try {
      const payload = {
        PROPERTY_ID: property.id,
        PROPERTY_UUID: propertyUuid,
        PROPERTY_TITLE: newTitle,
      };
      await callApi("PUT", "/properties/", payload);
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === property.id
            ? {
                ...row,
                propertyTitle: newTitle,
                raw: {
                  ...row.raw,
                  title: newTitle,
                },
              }
            : row
        )
      );
      setEditingTitleId(null);
      setEditingTitle("");
      setSuccessMsg("Property title updated successfully.");
    } catch (err) {
      console.error("Failed to update property title:", err);
      setApiError(err?.response?.data?.message || "Failed to update property title. Please try again.");
    } finally {
      setSavingTitle(false);
    }
  };

  const handleViewProperty = (property) => {
    const raw = property?.raw || {};
    const propertyUuid = property?.uuid || property?.property_uuid || raw?.uuid || raw?.PROPERTY_UUID || null;
    if (!propertyUuid) {
      setApiError("Cannot view property. Property UUID is missing.");
      return;
    }
    navigate(`/settings/property-management/view/${encodeURIComponent(propertyUuid)}`);
  };

  // ACTION MENU
  const renderActionsForRow = (rowId) => {
    const property = rowsWithBuyers.find((r) => r.id === rowId);
    const isVerifiedTab = activeTab === "verified";
    const isAlreadySold = (property?.status || "").toLowerCase() === "soldout";

    return (
      <OverflowMenu light flipped inlineIconDescription="More actions">
        <OverflowMenuItem
          itemText="View Property"
          onClick={() => property && handleViewProperty(property)}
        />
        <OverflowMenuItem
          itemText="Manage Property"
          onClick={() => property && handleEditProperty(property)}
          renderIcon={Edit}
        />
        <OverflowMenuItem
          itemText="Manage Contact"
          onClick={() => property && handleEditContact(property)}
          renderIcon={Edit}
        />
        <OverflowMenuItem
          itemText="Verify / Tags"
          onClick={() => property && handleVerify(property)}
        />
        <OverflowMenuItem
          itemText="Transactions"
          onClick={() => {
            const propertyUuid = property?.uuid || property?.property_uuid;
            if (propertyUuid) {
              setTransactionPropertyUuid(propertyUuid);
              setSelectedPropertyInfo({
                uuid: propertyUuid,
                title: property.propertyTitle,
                createdBy: property.createdBy,
                createdAt: property.createdAt,
                sequenceID: property.sequenceID,
              });
              fetchTransactionsByProperty(propertyUuid);
            }
          }}
        />

       <OverflowMenuItem
          itemText="View Stats"
          onClick={() => {
            const propertyUuid = property?.uuid || property?.property_uuid;
            if (propertyUuid) {
              navigate(`/settings/property-management/analytics/${encodeURIComponent(propertyUuid)}`, {
               state: { propertyTitle: property.propertyTitle },
              });
            }
          }}
        />

<OverflowMenuItem
  itemText="View Potential Buyers"
  onClick={async () => {
    if (!property) return;

    const uuid = property?.uuid || property?.property_uuid;
    if (!uuid) {
      setBuyersProperty(property);
      setBuyersModalError("Property UUID is missing.");
      setIsBuyersModalOpen(true);
      return;
    }

    // 1. Open the modal immediately so the spinner shows
    setBuyersProperty({
      ...property,
      potentialBuyers: [],
      potentialBuyersCount: 0,
    });
    setBuyersModalError("");
    setIsBuyersModalOpen(true);

    // 2. Fetch this property's leads on demand
    const { buyers, total } = await fetchPotentialBuyersForProperty(uuid);

    // 3. Populate the modal with the fetched data
    setBuyersProperty((prev) =>
      prev
        ? { ...prev, potentialBuyers: buyers, potentialBuyersCount: total }
        : prev
    );
  }}
/>
        {isVerifiedTab && !isAlreadySold && (
          <OverflowMenuItem
            itemText="Sold Out"
            onClick={() => {
              setSoldOutId(rowId);
              setIsSoldOutModalOpen(true);
            }}
          />
        )}
        <OverflowMenuItem
          itemText="Delete"
          onClick={() => confirmDelete(rowId)}
          renderIcon={TrashCan}
        />
      </OverflowMenu>
    );
  };

  // Search & Filter Logic
  useEffect(() => {
    const t = setTimeout(() => {
      const search = searchTerm.trim().toLowerCase();
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Merge potential buyers into rows
  const rowsWithBuyers = useMemo(() => {
    return rows.map((r) => {
      const key = r.uuid || r.property_uuid;
      const info = key ? potentialBuyersMap[key] : null;
      return {
        ...r,
        potentialBuyers: info?.buyers || [],
        potentialBuyersCount: info?.total || 0,
      };
    });
  }, [rows, potentialBuyersMap]);

  // Tab filtering
  const tabFilteredRows = useMemo(() => {
    const status = (v) => (v || "").toLowerCase().trim();

    if (activeTab === "pending") {
      return rowsWithBuyers.filter((r) =>
        ["pending", "draft", "pending for verification"].includes(status(r.verificationStatus))
      );
    }
    if (activeTab === "re-verification") {
      return rowsWithBuyers.filter((r) =>
        ["re_verification", "re verification", "re-verification"].includes(status(r.verificationStatus))
      );
    }
    if (activeTab === "under_verification") {
      return rowsWithBuyers.filter((r) => isUnderVerification(r.verificationStatus));
    }
    if (activeTab === "verified") {
      return rowsWithBuyers.filter(
        (r) =>
          status(r.verificationStatus) === "verified" &&
          status(r.status) !== "soldout"
      );
    }
    if (activeTab === "soldout") {
      return rowsWithBuyers.filter((r) => status(r.status) === "soldout");
    }
    if (activeTab === "expired") {
      return rowsWithBuyers.filter((r) => status(r.status) === "expired");
    }
    return [];
  }, [rowsWithBuyers, activeTab]);

  // Search and filter logic
  const filteredRows = useMemo(() => {
   let filtered = tabFilteredRows; 
if (debouncedSearch) {
  const s = debouncedSearch;
  filtered = filtered.filter((r) => {
    // Helper to safely convert anything (string, array, number) to searchable text
    const text = (v) => {
      if (v === null || v === undefined) return "";
      if (Array.isArray(v)) return v.join(" ");
      return String(v);
    };

    // Combine all searchable fields into one big string
    const haystack = [
      r.propertyTitle,
      r.postedFor,
      r.postedBy,
      r.postedType,
      r.status,
      r.createdBy,
      r.description,
      r.sequenceID,
      r.verificationStatus,
      r.priceInfo,          
      r.paymentStatus,      
      r.tag,                // ✅ now searchable (array)
      r.location,           // ✅ now searchable
      r.verifiedBy,         // ✅ now searchable
      r.verifiedDate,       // ✅ now searchable
      r.createdAt,          // ✅ now searchable
      r.updatedAt,          // ✅ now searchable
      r.ownerNumber,        // ✅ contact number
      r.type,               // raw type
      r.priceMeasurementUnit,
      r.startingPrice,
      r.endingPrice,
    ]
      .map(text)
      .join(" ")
      .toLowerCase();

    return haystack.includes(s);
  });
}

    filtered = applyFilters(filtered);
    return filtered;
  }, [tabFilteredRows, debouncedSearch, applyFilters]);

  const totalItems = filteredRows.length;
  const startIndex = (page - 1) * pageSize;
  const paginatedRows = useMemo(() => {
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, startIndex, pageSize]);

  return (
    <>
      {successMsg && (
        <InlineNotification
          kind="success"
          title="Success"
          subtitle={successMsg}
          onCloseButtonClick={() => setSuccessMsg("")}
          style={{ marginBottom: "1rem" }}
        />
      )}
      
<PropertyFilters
  filters={filters}
  dropdownOptions={dropdownOptions}
  verificationOptionsByTab={verificationOptionsByTab}
  statusOptionsByTab={statusOptionsByTab}
  handleFilterChange={handleFilterChange}
  clearAllFilters={clearAllFilters}
  activeTab={activeTab}
  builderOptions={[
    { id: "yes", text: "Builder" },
  ]}
/>

      <TableContainer title="Property List">
        <Tabs
          selectedIndex={
            activeTab === "pending"
              ? 0
              : activeTab === "re-verification"
              ? 1
              : activeTab === "under_verification"
              ? 2
              : activeTab === "verified"
              ? 3
              : activeTab === "soldout"
              ? 4
              : activeTab === "expired"
              ? 5
              : 0
          }
          onChange={({ selectedIndex }) => {
            if (selectedIndex === 0) handleTabChange("pending");
            else if (selectedIndex === 1) handleTabChange("re-verification");
            else if (selectedIndex === 2) handleTabChange("under_verification");
            else if (selectedIndex === 3) handleTabChange("verified");
            else if (selectedIndex === 4) handleTabChange("soldout");
            else if (selectedIndex === 5) handleTabChange("expired");
          }}
        >
          <TabList aria-label="Property verification tabs">
            <Tab>
              Pending
              <Tag size="sm" type="red" style={{ marginLeft: 8 }}>
                {tabCounts.pending}
              </Tag>
            </Tab>
            <Tab>
              Re-Verification
              <Tag size="sm" type="magenta" style={{ marginLeft: 8 }}>
                {tabCounts.reVerification}
              </Tag>
            </Tab>
            <Tab>
              Under Verification
              <Tag size="sm" type="cyan" style={{ marginLeft: 8 }}>
                {tabCounts.underVerification}
              </Tag>
            </Tab>
            <Tab>
              Verified
              <Tag size="sm" type="green" style={{ marginLeft: 8 }}>
                {tabCounts.verified}
              </Tag>
            </Tab>
            <Tab>
              Sold Out
              <Tag size="sm" type="gray" style={{ marginLeft: 8 }}>
                {tabCounts.soldOut}
              </Tag>
            </Tab>
            <Tab>
              Expired
              <Tag size="sm" type="gray" style={{ marginLeft: 8 }}>
                {tabCounts.expired}
              </Tag>
            </Tab>
          </TabList>
        </Tabs>

        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch
              persistent
              id="property-search"
              placeHolderText="Filter table"
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                setPage(1);
              }}
              value={searchTerm}
            />

            {/* NEW: Filter toggle button */}
<Button
  kind="ghost"
  renderIcon={Filter}
  onClick={() => setShowFilters((prev) => !prev)}
>
  {showFilters ? "Hide Filters" : "Filters"}
</Button>

            <Button renderIcon={Add} onClick={() => setIsAddModalOpen(true)}>
              Add Property
            </Button>
          </TableToolbarContent>
        </TableToolbar>
<div className="property-table-wrapper">

        <PropertyTable
          headers={visibleHeaders}
          rows={paginatedRows}
          loading={loading}
          renderActionsForRow={renderActionsForRow}
          totalItems={totalItems}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          editingTitleId={editingTitleId}
          editingTitle={editingTitle}
          setEditingTitle={setEditingTitle}
          savingTitle={savingTitle}
          handleEditTitle={handleEditTitle}
          handleSaveTitle={handleSaveTitle}
          handleCancelTitleEdit={handleCancelTitleEdit}
        />
        </div>

        {/* Add Property Modal */}
        <AddModal
          isAddModalOpen={isAddModalOpen}
          setIsAddModalOpen={setIsAddModalOpen}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedAdType={selectedAdType}
          setSelectedAdType={setSelectedAdType}
          PROPERTY_TYPES={PROPERTY_TYPES}
          modalForm={modalForm}
          dropdownOptions={dropdownOptions}
          handleModalChange={handleModalChange}
          validateNumericInput={validateNumericInput}
          findSelectedItem={findSelectedItem}
          requiredLabel={requiredLabel}
          handleStartPosting={handleStartPosting}
          isContactFormValid={isContactFormValid}
        />

        {/* Edit Contact Modal */}
        <ContactModal
          isEditContactModalOpen={isEditContactModalOpen}
          setIsEditContactModalOpen={setIsEditContactModalOpen}
          editingProperty={editingProperty}
          editContactForm={editContactForm}
          dropdownOptions={dropdownOptions}
          savingContact={savingContact}
          handleEditContactChange={handleEditContactChange}
          handleUpdateContact={handleUpdateContact}
          findSelectedItem={findSelectedItem}
          requiredLabel={requiredLabel}
          validateNumericInput={validateNumericInput}
        />

        {/* Delete Modal */}
        <Modal
          open={isDeleteModalOpen}
          danger
          modalHeading="Confirm Delete"
          primaryButtonText={deletingId ? "" : "Delete"}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={!deleteReason.trim() || deletingId}
          onRequestClose={() => {
            if (!deletingId) {
              setIsDeleteModalOpen(false);
              setDeleteReason("");
            }
          }}
          onRequestSubmit={handleDeleteConfirmed}
        >
          <p style={{ marginBottom: "1rem" }}>
            Are you sure you want to delete this property? This action cannot be undone.
          </p>
          <FormGroup>
            <TextInput
              id="delete-reason"
              labelText={requiredLabel("Reason for Cancellation ")}
              placeholder="Enter reason for deleting this property"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              disabled={deletingId}
            />
          </FormGroup>
          {deletingId && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
              <InlineLoading description="Deleting..." />
              <span>Deleting property...</span>
            </div>
          )}
        </Modal>

        {/* Verify Modal */}
        <PropertyVerifyModal
          isVerifyModalOpen={isVerifyModalOpen}
          verifyProperty={verifyProperty}
          verifyForm={verifyForm}
          apiError={apiError}
          setApiError={setApiError}
          setVerifyForm={setVerifyForm}
          dropdownOptions={{ ...dropdownOptions, subscriptionPlans }}
          propertyDocuments={propertyDocuments}
          loadingDocuments={loadingDocuments}
          verifyingId={verifyingId}
          handleVerifyConfirmed={handleVerifyConfirmed}
          setIsVerifyModalOpen={setIsVerifyModalOpen}
          handleDownloadDocument={handleDownloadDocument}
          handleViewDocument={handleViewDocument}
          onRemovePlan={handleRemovePlan}  
        />

          {/* Transaction Modal */}
        <TransactionModal
          isTransactionModalOpen={isTransactionModalOpen}
          setIsTransactionModalOpen={setIsTransactionModalOpen}
          loadingTransactions={loadingTransactions}
          apiError={apiError}
          transactionRows={transactionRows}
          propertyInfo={selectedPropertyInfo}
        />

        {/* Potential Buyers Modal */}
        <Modal
          open={isBuyersModalOpen}
          passiveModal
          modalHeading={`Potential Buyers — ${buyersProperty?.propertyTitle || ""}`}
          onRequestClose={() => {
            setIsBuyersModalOpen(false);
            setBuyersProperty(null);
            setBuyersModalError("");
          }}
        >
          {loadingPotentialBuyers ? (
            <InlineLoading description="Loading potential buyers..." />
          ) : buyersModalError ? (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={buyersModalError}
              onCloseButtonClick={() => setBuyersModalError("")}
            />
          ) : buyersProperty?.potentialBuyers?.length ? (
            <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
              <p style={{ marginBottom: "0.75rem", color: "#525252" }}>
                Total: <strong>{buyersProperty.potentialBuyersCount}</strong>
              </p>
              {buyersProperty.potentialBuyers.map((b, idx) => (
                <div
                  key={`${b.user_id || idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 0",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  {b.profilePic ? (
                    <img
                      src={b.profilePic}
                      alt={b.name || "Buyer"}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "";
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: "#e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        color: "#161616",
                        flexShrink: 0,
                      }}
                    >
                      {(b.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, wordBreak: "break-word" }}>
                      {b.name || "Unnamed"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#525252",
                        wordBreak: "break-word",
                      }}
                    >
                      {b.email || "No email"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: "#525252" }}>No potential buyers yet for this property.</p>
          )}
        </Modal>


        {/* Sold Out Modal */}
        <Modal
          open={isSoldOutModalOpen}
          danger
          modalHeading="Mark Property as Sold Out"
          primaryButtonText={markingSoldOut ? "" : "Yes/Continue"}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={!soldOutReason.trim() || markingSoldOut}
          onRequestClose={() => {
            if (!markingSoldOut) {
              setIsSoldOutModalOpen(false);
              setSoldOutReason("");
            }
          }}
          onRequestSubmit={handleSoldOutConfirmed}
        >
          <p style={{ marginBottom: "1rem" }}>
            Are you sure you want to mark this property as Sold Out? This action cannot be undone.
          </p>
          <FormGroup>
            <TextInput
              id="sold-out-reason"
              labelText={requiredLabel("Reason")}
              placeholder="Enter reason"
              value={soldOutReason}
              onChange={(e) => setSoldOutReason(e.target.value)}
              disabled={markingSoldOut}
            />
          </FormGroup>
          {markingSoldOut && (
            <div style={{ marginTop: 12 }}>
              <InlineLoading description="Updating status..." />
            </div>
          )}
        </Modal>
      </TableContainer>
    </>
  );
}